import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import logger from '../utils/logger';
import { useLanguage } from '../context/LanguageContext';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import { ALL_CATEGORIES } from './useAdminData';
import { API_URL } from '../utils/apiConfig';
import { optimisticSave } from '../utils/optimisticSave';

export const INITIAL_FORM = {
  name_simple_fr: '',
  name_simple_de: '',
  source_product_name: '',
  subcategory: '',
  alternative_subcategories: [],
  input_units: [''],
  default_unit: '',
  impacts: [],
  unit_conversions: {},
  tags: '',
  search_tags: [],
  source: 'OFEV',
  region: 'Suisse',
  year: 2024,
  is_public: true,
  reporting_method: '',
  popularity_score: 50
};

export const IMPACT_TYPES_CONFIG = [
  { key: 'scope1', labelKey: 'admin.factors.scope1Direct', scope: 'scope1', type: 'direct', descKey: 'scope.scope1Title' },
  { key: 'scope2', labelKey: 'admin.factors.scope2Indirect', scope: 'scope2', type: 'indirect', descKey: 'scope.scope2Title' },
  { key: 'scope3_3', labelKey: 'admin.factors.scope33Upstream', scope: 'scope3_amont', type: 'upstream', descKey: 'scope.scope3AmontTitle', category: 'activites_combustibles_energie' },
  { key: 'scope3', labelKey: 'admin.factors.scope3Other', scope: 'scope3_amont', type: 'upstream', descKey: 'scope.scope3AvalTitle' }
];

const useAdminFactors = ({ subcategories, onPageChange, onRefetch }) => {
  const { t } = useLanguage();
  const { showError, showSuccess } = useToast();
  const { confirm } = useConfirm();

  const [search, setSearch] = useState('');
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [expertFilter, setExpertFilter] = useState('all');

  const [showFactorModal, setShowFactorModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const [editingFactor, setEditingFactor] = useState(null);
  const [factorForm, setFactorForm] = useState(INITIAL_FORM);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [importData, setImportData] = useState('');
  const [importReplaceAll, setImportReplaceAll] = useState(false);
  const [versioningFactor, setVersioningFactor] = useState(null);
  const [versionForm, setVersionForm] = useState({
    change_reason: '',
    is_correction: false,
    valid_from_year: new Date().getFullYear(),
    impacts: []
  });
  const [factorHistory, setFactorHistory] = useState(null);

  // Debounced search
  const handleSearchChange = useCallback((value) => {
    setSearch(value);
    if (searchTimeout) clearTimeout(searchTimeout);
    const isPublicParam = expertFilter === 'public' ? 'true' : expertFilter === 'expert' ? 'false' : '';
    const timeout = setTimeout(() => {
      onPageChange(1, value, isPublicParam);
    }, 400);
    setSearchTimeout(timeout);
  }, [onPageChange, searchTimeout, expertFilter]);

  const handleFilterChange = useCallback((filter) => {
    setExpertFilter(filter);
    const isPublicParam = filter === 'public' ? 'true' : filter === 'expert' ? 'false' : '';
    onPageChange(1, search, isPublicParam);
  }, [onPageChange, search]);

  useEffect(() => {
    return () => { if (searchTimeout) clearTimeout(searchTimeout); };
  }, [searchTimeout]);

  // Linked categories helpers
  const getLinkedCategories = useCallback(() => {
    if (!factorForm.subcategory) return [];
    const subcat = subcategories.find(s => s.code === factorForm.subcategory);
    return subcat?.categories || [];
  }, [factorForm.subcategory, subcategories]);

  const getCategoriesForImpactType = useCallback((impactKey) => {
    const linkedCats = getLinkedCategories();
    const config = IMPACT_TYPES_CONFIG.find(c => c.key === impactKey);
    if (!config) return [];

    if (impactKey === 'scope3_3') {
      return [{ value: 'activites_combustibles_energie', label: t('categories.activites_combustibles_energie') }];
    }

    return ALL_CATEGORIES.filter(cat => {
      if (impactKey === 'scope1') return linkedCats.includes(cat.value) && cat.scope === 'scope1';
      if (impactKey === 'scope2') return linkedCats.includes(cat.value) && cat.scope === 'scope2';
      if (impactKey === 'scope3') {
        return linkedCats.includes(cat.value) &&
               (cat.scope === 'scope3_amont' || cat.scope === 'scope3_aval') &&
               cat.value !== 'activites_combustibles_energie';
      }
      return false;
    }).map(cat => ({ ...cat, label: t(`categories.${cat.value}`) }));
  }, [getLinkedCategories, t]);

  // Subcategory change - just update subcategory, user fills impacts explicitly via the 4-scope UI
  const handleSubcategoryChange = useCallback((newSubcategory) => {
    setFactorForm(prev => ({ ...prev, subcategory: newSubcategory }));
  }, []);

  const updateImpactByKey = useCallback((impactKey, field, value) => {
    setFactorForm(prev => ({
      ...prev,
      impacts: prev.impacts.map(imp => imp.impactKey !== impactKey ? imp : { ...imp, [field]: value })
    }));
  }, []);

  const addInputUnit = useCallback(() => {
    setFactorForm(prev => ({ ...prev, input_units: [...prev.input_units, ''] }));
  }, []);

  const removeInputUnit = useCallback((index) => {
    if (factorForm.input_units.length <= 1) return;
    setFactorForm(prev => ({ ...prev, input_units: prev.input_units.filter((_, i) => i !== index) }));
  }, [factorForm.input_units.length]);

  const resetFactorForm = useCallback(() => setFactorForm(INITIAL_FORM), []);

  // CRUD
  const handleSaveFactor = useCallback(async () => {
    try {
      const factorData = {
        ...factorForm,
        name_simple_fr: factorForm.name_simple_fr || null,
        name_simple_de: factorForm.name_simple_de || null,
        source_product_name: factorForm.source_product_name || null,
        reporting_method: factorForm.reporting_method || null,
        popularity_score: parseInt(factorForm.popularity_score) || 50,
        tags: factorForm.tags.split(',').map(tag => tag.trim()).filter(Boolean),
        search_tags: factorForm.search_tags || [],
        impacts: factorForm.impacts.map(imp => ({
          scope: imp.impactKey || imp.scope,
          value: parseFloat(imp.value) || 0,
          unit: imp.unit,
          category: imp.category || null,
        })),
        input_units: factorForm.input_units.filter(Boolean),
        is_public: factorForm.is_public
      };

      if (editingFactor) {
        const result = await optimisticSave(t, () =>
          axios.put(`${API_URL}/api/admin/emission-factors-v2/${editingFactor.id}`, {
            ...factorData,
            updated_at: editingFactor?.updated_at,
          })
        );
        if (!result.ok) {
          onRefetch();
          return;
        }
      } else {
        await axios.post(`${API_URL}/api/admin/emission-factors-v2`, factorData);
      }

      setShowFactorModal(false);
      setEditingFactor(null);
      resetFactorForm();
      onRefetch();
    } catch (error) {
      logger.error('Failed to save factor:', error);
      showError(t('errors.generic') + ': ' + (error.response?.data?.detail || error.message));
    }
  }, [factorForm, editingFactor, resetFactorForm, onRefetch, t, showError]);

  const handleEditFactor = useCallback((factor) => {
    setEditingFactor(factor);
    const impacts = factor.impacts || [{ scope: factor.scope, category: factor.category, value: factor.value, unit: factor.unit }];
    const subcat = subcategories.find(s => s.code === factor.subcategory);
    const linkedCats = subcat?.categories || [];

    setFactorForm({
      name_simple_fr: factor.name_simple_fr || '',
      name_simple_de: factor.name_simple_de || '',
      source_product_name: factor.source_product_name || '',
      subcategory: factor.subcategory || '',
      alternative_subcategories: factor.alternative_subcategories || [],
      input_units: factor.input_units?.length ? factor.input_units : [''],
      default_unit: factor.default_unit || '',
      impacts: impacts.map((imp) => {
        const impactKey = imp.scope === 'scope1' ? 'scope1' : imp.scope === 'scope2' ? 'scope2' : (imp.category === 'activites_combustibles_energie' || imp.scope === 'scope3_3') ? 'scope3_3' : 'scope3';
        let category = imp.category;
        if (!category) {
          if (impactKey === 'scope3_3') {
            category = 'activites_combustibles_energie';
          } else {
            const found = ALL_CATEGORIES.find(c => {
              if (!linkedCats.includes(c.value)) return false;
              if (impactKey === 'scope1') return c.scope === 'scope1';
              if (impactKey === 'scope2') return c.scope === 'scope2';
              return (c.scope === 'scope3_amont' || c.scope === 'scope3_aval') && c.value !== 'activites_combustibles_energie';
            });
            category = found?.value || '';
          }
        }
        return { impactKey, ...imp, category, value: imp.value?.toString() || '' };
      }),
      unit_conversions: factor.unit_conversions || {},
      tags: (factor.tags || []).join(', '),
      source: factor.source || 'OFEV',
      region: factor.region || 'Suisse',
      year: factor.year || 2024,
      is_public: factor.is_public !== false,
      reporting_method: factor.reporting_method || '',
      popularity_score: factor.popularity_score ?? 50
    });
    setShowFactorModal(true);
  }, [subcategories]);

  const handleDuplicateFactor = useCallback((factor) => {
    setEditingFactor(null);
    const impacts = factor.impacts || [{ scope: factor.scope, category: factor.category, value: factor.value, unit: factor.unit }];
    const subcat = subcategories.find(s => s.code === factor.subcategory);
    const linkedCats = subcat?.categories || [];

    setFactorForm({
      name_simple_fr: factor.name_simple_fr ? `${factor.name_simple_fr} (copie)` : '',
      name_simple_de: factor.name_simple_de ? `${factor.name_simple_de} (Kopie)` : '',
      source_product_name: factor.source_product_name || '',
      subcategory: factor.subcategory || '',
      alternative_subcategories: factor.alternative_subcategories || [],
      input_units: factor.input_units?.length ? [...factor.input_units] : [''],
      default_unit: factor.default_unit || '',
      impacts: impacts.map((imp) => {
        const impactKey = imp.scope === 'scope1' ? 'scope1' : imp.scope === 'scope2' ? 'scope2' : (imp.category === 'activites_combustibles_energie' || imp.scope === 'scope3_3') ? 'scope3_3' : 'scope3';
        let category = imp.category;
        if (!category) {
          if (impactKey === 'scope3_3') {
            category = 'activites_combustibles_energie';
          } else {
            const found = ALL_CATEGORIES.find(c => {
              if (!linkedCats.includes(c.value)) return false;
              if (impactKey === 'scope1') return c.scope === 'scope1';
              if (impactKey === 'scope2') return c.scope === 'scope2';
              return (c.scope === 'scope3_amont' || c.scope === 'scope3_aval') && c.value !== 'activites_combustibles_energie';
            });
            category = found?.value || '';
          }
        }
        return { impactKey, ...imp, category, value: imp.value?.toString() || '' };
      }),
      unit_conversions: factor.unit_conversions ? { ...factor.unit_conversions } : {},
      tags: (factor.tags || []).join(', '),
      source: factor.source || 'OFEV',
      region: factor.region || 'Suisse',
      year: factor.year || 2024,
      is_public: factor.is_public !== false,
      reporting_method: factor.reporting_method || '',
      popularity_score: factor.popularity_score ?? 50
    });
    setShowFactorModal(true);
  }, [subcategories]);

  const handleDeleteFactor = useCallback(async (factorId) => {
    if (!await confirm({ message: t('confirmations.delete'), variant: 'danger' })) return;
    try {
      await axios.delete(`${API_URL}/api/admin/emission-factors-v2/${factorId}`);
      onRefetch();
    } catch (error) {
      logger.error('Failed to delete factor:', error);
      showError(t('errors.generic'));
    }
  }, [onRefetch, t, showError, confirm]);

  const handleSoftDelete = useCallback(async (factorId) => {
    if (!await confirm({ message: t('confirmations.archive'), variant: 'warning' })) return;
    try {
      await axios.delete(`${API_URL}/api/admin/emission-factors-v2/${factorId}/soft`);
      onRefetch();
    } catch (error) {
      logger.error('Failed to archive factor:', error);
      showError(t('errors.generic') + ': ' + (error.response?.data?.detail || error.message));
    }
  }, [onRefetch, t, showError, confirm]);

  // Versioning
  const handleCreateNewVersion = useCallback((factor) => {
    setVersioningFactor(factor);
    setVersionForm({
      change_reason: '',
      is_correction: false,
      valid_from_year: new Date().getFullYear(),
      impacts: (factor.impacts || []).map(imp => ({ ...imp, value: imp.value?.toString() || '' }))
    });
    setShowVersionModal(true);
  }, []);

  const handleSaveNewVersion = useCallback(async () => {
    if (!versioningFactor) return;
    try {
      const payload = {
        change_reason: versionForm.change_reason,
        is_correction: versionForm.is_correction,
        valid_from_year: versionForm.valid_from_year,
        impacts: versionForm.impacts.map(imp => ({
          scope: imp.scope,
          category: imp.category,
          value: parseFloat(imp.value),
          unit: imp.unit,
          type: imp.type || 'direct'
        }))
      };
      await axios.post(`${API_URL}/api/admin/emission-factors-v2/${versioningFactor.id}/new-version`, payload);
      setShowVersionModal(false);
      setVersioningFactor(null);
      onRefetch();
    } catch (error) {
      logger.error('Failed to create new version:', error);
      showError(t('errors.generic') + ': ' + (error.response?.data?.detail || error.message));
    }
  }, [versioningFactor, versionForm, onRefetch, t, showError]);

  const updateVersionImpact = useCallback((index, field, value) => {
    setVersionForm(prev => ({
      ...prev,
      impacts: prev.impacts.map((imp, i) => i === index ? { ...imp, [field]: value } : imp)
    }));
  }, []);

  // History
  const handleViewHistory = useCallback(async (factorId) => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/emission-factors-v2/${factorId}/history`);
      setFactorHistory(response.data);
      setShowHistoryModal(true);
    } catch (error) {
      logger.error('Failed to fetch history:', error);
      showError(t('errors.generic') + ': ' + (error.response?.data?.detail || error.message));
    }
  }, [t, showError]);

  // Import/Export
  const handleExportV2 = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/emission-factors-v2/export`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const blob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `emission_factors_v2_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch (error) {
      logger.error('Export failed:', error);
      showError(t('errors.generic'));
    }
  }, [t, showError]);

  const handleImportV2 = useCallback(async () => {
    try {
      const data = JSON.parse(importData);
      await axios.post(`${API_URL}/api/admin/emission-factors-v2/import`, { ...data, replace_all: importReplaceAll });
      setShowImportModal(false);
      setImportData('');
      onRefetch();
      showSuccess(t('admin.import.success'));
    } catch (error) {
      logger.error('Import failed:', error);
      showError(t('errors.generic') + ': ' + error.message);
    }
  }, [importData, importReplaceAll, onRefetch, t, showSuccess, showError]);

  const openNewFactorModal = useCallback(() => {
    resetFactorForm();
    setEditingFactor(null);
    setShowFactorModal(true);
  }, [resetFactorForm]);

  return {
    search, handleSearchChange,
    expertFilter, handleFilterChange,
    showFactorModal, setShowFactorModal,
    showImportModal, setShowImportModal,
    showVersionModal, setShowVersionModal,
    showHistoryModal, setShowHistoryModal,
    editingFactor, factorForm, setFactorForm,
    showAdvanced, setShowAdvanced,
    openNewFactorModal,
    handleSaveFactor, handleEditFactor, handleDuplicateFactor,
    handleDeleteFactor, handleSoftDelete,
    handleSubcategoryChange, updateImpactByKey,
    addInputUnit, removeInputUnit,
    getLinkedCategories, getCategoriesForImpactType,
    versioningFactor, versionForm, setVersionForm,
    handleCreateNewVersion, handleSaveNewVersion, updateVersionImpact,
    factorHistory, handleViewHistory,
    importData, setImportData,
    importReplaceAll, setImportReplaceAll,
    handleExportV2, handleImportV2,
  };
};

export default useAdminFactors;
